import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchProyectosSozuIds } from "@/hooks/usePortalAltaDireccion/proyectosSozu";

export interface ProyectoAcceso {
  id: number;
  nombre: string;
  /** Proyecto propio de SOZU. */
  sozu: boolean;
}

export interface ProyectosConAcceso {
  /** Super Admin (rol 1 o 2): ve todos los proyectos. */
  todos: boolean;
  cargando: boolean;
  /** Los de SOZU primero (del lanzamiento más reciente al más viejo), luego por nombre. */
  lista: ProyectoAcceso[];
}

type FilaProyecto = { id: number; nombre: string; publicar: boolean | null; fecha_lanzamiento: string | null };
const COLUMNAS = "id, nombre, publicar, fecha_lanzamiento";

/**
 * Propio de SOZU = lo comercializa SOZU (entidad tipo 5) y está publicado, igual que en Reglas de
 * cobro. Sale de la BD: un proyecto nuevo entra solo, sin tocar código.
 */
const ordenar = (rows: FilaProyecto[], idsSozu: Set<number>): ProyectoAcceso[] =>
  rows
    .map((p) => ({ id: p.id, nombre: p.nombre, sozu: idsSozu.has(p.id) && p.publicar === true, lanzamiento: p.fecha_lanzamiento ?? "" }))
    .sort((a, b) =>
      Number(b.sozu) - Number(a.sozu)
      || (a.sozu ? b.lanzamiento.localeCompare(a.lanzamiento) : 0)
      || a.nombre.localeCompare(b.nombre, "es"))
    .map(({ lanzamiento: _lanzamiento, ...p }) => p);

/** PostgREST corta en 1000 filas: se pide el conteo y luego los bloques necesarios. */
const BLOQUE = 1000;
async function todosLosProyectosActivos() {
  const { count } = await supabase.from("proyectos").select("id", { count: "exact", head: true }).eq("activo", true);
  const bloques = Math.max(1, Math.ceil((count ?? 0) / BLOQUE));
  const partes = await Promise.all(
    Array.from({ length: bloques }, (_, i) =>
      supabase.from("proyectos").select(COLUMNAS).eq("activo", true).order("id").range(i * BLOQUE, (i + 1) * BLOQUE - 1),
    ),
  );
  return partes.flatMap((r) => (r.data ?? []) as FilaProyecto[]);
}

/** Proyectos a los que tiene acceso el usuario: los de proyectos_acceso, o todos los activos si su rol los ve todos. */
export function useProyectosConAcceso(
  enabled: boolean,
  /** "Ver como": los proyectos de la persona vista (su correo y su rol), no los de la sesión. */
  vista?: { email: string; rolId: number | null } | null,
): ProyectosConAcceso {
  const { profile } = useAuth();
  const email = vista?.email ?? profile?.email;
  const rolId = vista ? vista.rolId : profile?.rol_id;
  const todos = rolId === 1 || rolId === 2;

  const { data, isLoading } = useQuery({
    queryKey: ["user_projects_access", email, todos],
    enabled: enabled && !!email,
    queryFn: async () => {
      const idsSozu = await fetchProyectosSozuIds().catch(() => new Set<number>());
      if (todos) return ordenar(await todosLosProyectosActivos(), idsSozu);
      const { data: accesos } = await supabase
        .from("proyectos_acceso")
        .select("proyecto_id")
        .eq("usuario_id", email!)
        .eq("activo", true);
      const ids = (accesos ?? []).map((a) => a.proyecto_id);
      if (ids.length === 0) return [];
      const { data: proyectos } = await supabase
        .from("proyectos")
        .select(COLUMNAS)
        .in("id", ids)
        .eq("activo", true);
      return ordenar((proyectos ?? []) as FilaProyecto[], idsSozu);
    },
  });

  return { todos, cargando: isLoading, lista: data ?? [] };
}
